import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Marquee from "react-fast-marquee";
import { PLACEHOLDER } from "../data/site";

export const PlaceholderTag = ({ text = PLACEHOLDER, dark = false }) => (
  <span
    data-testid="content-placeholder"
    className={`inline-flex w-fit items-center px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest ${
      dark ? "bg-white/15 text-white/80" : "bg-charcoal/10 text-charcoal/70"
    }`}
  >
    {text}
  </span>
);

export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const MaskedLines = ({ lines, className = "", as: Tag = "h1" }) => (
  <Tag className={className}>
    {lines.map((line, i) => (
      <span key={i} className="block overflow-hidden pb-1">
        <motion.span
          className="block"
          initial={{ y: "115%" }}
          animate={{ y: 0 }}
          transition={{ duration: 1, delay: 0.25 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </Tag>
);

export const SectionHead = ({ overline, title, copy, dark = false }) => (
  <Reveal className="max-w-3xl">
    <p className={`text-xs font-bold uppercase tracking-[0.2em] ${dark ? "text-white/60" : "text-charcoal/60"}`}>
      {overline}
    </p>
    <h2
      className={`mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-tight ${
        dark ? "text-warmwhite" : "text-charcoal"
      }`}
    >
      {title}
    </h2>
    {copy && (
      <p className={`mt-6 text-base sm:text-lg leading-relaxed ${dark ? "text-white/70" : "text-charcoal/70"}`}>
        {copy}
      </p>
    )}
  </Reveal>
);

export const TempImage = ({ src, alt, ratio = "aspect-[16/9]", className = "", dark = false, real = false }) => (
  <figure className={className}>
    <div className={`overflow-hidden rounded-2xl bg-softgray ${ratio}`}>
      <img src={src} alt={alt} className="w-full h-full object-cover" loading="lazy" />
    </div>
    {!real && (
      <figcaption className="mt-3">
        <PlaceholderTag text="Temporary placeholder image" dark={dark} />
      </figcaption>
    )}
  </figure>
);

export const EditorialMarquee = ({ items }) => (
  <div data-testid="editorial-marquee" className="border-y border-charcoal/10 py-6 md:py-8 overflow-hidden" aria-hidden="true">
    <Marquee speed={25} gradient={false}>
      {items.concat(items).map((item, i) => (
        <span key={i} className="flex items-center whitespace-nowrap">
          <span className="mx-10 font-serif text-2xl md:text-4xl font-light italic text-charcoal/70">{item}</span>
          <span className="h-1.5 w-1.5 rounded-full bg-terracotta" />
        </span>
      ))}
    </Marquee>
  </div>
);

export const StatBlock = ({ label, value, note, index }) => (
  <Reveal delay={index * 0.08}>
    <div data-testid={`impact-stat-${index}`} className="border-t border-charcoal/15 pt-6">
      <p className="font-serif text-6xl md:text-7xl font-light text-charcoal/90 leading-none">
        {value ?? "—"}
      </p>
      <p className="mt-4 text-sm font-semibold uppercase tracking-[0.15em] text-charcoal/60">{label}</p>
      {note && <p className="mt-2 text-xs text-charcoal/45">{note}</p>}
      {value == null && <div className="mt-3"><PlaceholderTag text="Figure to be updated" /></div>}
    </div>
  </Reveal>
);

export const StoryCard = ({ story, index }) => (
  <Reveal delay={index * 0.1} className="h-full">
    <article
      data-testid={`impact-story-${index}`}
      className="group bg-white border border-charcoal/5 rounded-2xl overflow-hidden flex flex-col h-full shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-shadow duration-500"
    >
      <div className="aspect-[16/10] overflow-hidden bg-softgray">
        <img
          src={story.image.url}
          alt={story.image.alt}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </div>
      <div className="p-7 md:p-8 flex flex-col gap-4 grow">
        {story.real ? (
          <span className="inline-flex w-fit items-center px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest bg-terracotta/10 text-terracotta">
            Documented event
          </span>
        ) : (
          <PlaceholderTag text="Placeholder story — content to be provided" />
        )}
        <h3 className="font-serif text-2xl font-medium tracking-tight text-charcoal">
          {story.title ?? "Project title to be provided"}
        </h3>
        <p className="text-sm leading-relaxed text-charcoal/70">
          {story.description ?? "A short documentary account of this project will be published here once official information is supplied by the Trust."}
        </p>
        <div className="mt-auto flex flex-wrap items-center gap-3 pt-2 text-xs font-mono uppercase tracking-widest text-charcoal/50">
          <span>{story.location ?? "Location TBC"}</span>
          <span className="h-1 w-1 rounded-full bg-charcoal/30" />
          <span>{story.year ?? "Year TBC"}</span>
        </div>
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-terracotta">
          Read More <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </div>
    </article>
  </Reveal>
);

export const PageHero = ({ overline, title, copy }) => (
  <section className="pt-40 md:pt-52 pb-16 md:pb-24">
    <div className="max-w-[90rem] mx-auto px-6 md:px-12">
      <Reveal>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-charcoal/60">{overline}</p>
      </Reveal>
      <MaskedLines
        lines={[title]}
        className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[1.1] text-charcoal max-w-4xl"
      />
      {copy && (
        <Reveal delay={0.3}>
          <p className="mt-8 max-w-2xl text-base sm:text-lg leading-relaxed text-charcoal/70">{copy}</p>
        </Reveal>
      )}
    </div>
  </section>
);
